// "My year": a personal planner, one row per month of the leave year.

import { useState } from 'react';
import { daysInMonth, fmtRange, formatDays, leaveYearLabel, leaveYearOf, monthISO, monthName } from '../lib/dates';
import { ACTIVE_STATUSES, balanceFor, displayType, isNonWorking, partOnDay, requestDays, typeOf } from '../lib/leave';
import { useStore } from '../store/StoreProvider';
import { useCtx } from '../store/useCtx';
import type { LeaveRequest } from '../types';
import { Avatar, byName, typeStyle } from '../ui/bits';
import { useDialogs } from '../ui/dialogs';
import { Icon } from '../ui/Icon';

export function Planner() {
  const ctx = useCtx();
  const store = useStore();
  const { openDetail } = useDialogs();
  const [pickedYear, setPickedYear] = useState<number | null>(null);
  const [pickedUser, setPickedUser] = useState<string | null>(null);

  const startMonth = ctx.settings.leaveYearStartMonth;
  const isHR = ctx.me.role === 'hr';
  const ly = pickedYear ?? leaveYearOf(ctx.today, startMonth);
  const user = (isHR && pickedUser && ctx.userById.get(pickedUser)) || ctx.me;
  const minYear = leaveYearOf(store.windowStart(), startMonth);
  const bal = balanceFor(user, ly, ctx);
  const mine = ctx.requests.filter((r) => r.userId === user.id && ACTIVE_STATUSES.includes(r.status));

  const months = Array.from({ length: 12 }, (_, i) => {
    const idx = startMonth - 1 + i;
    return { m: (idx % 12) + 1, y: ly + Math.floor(idx / 12) };
  });

  return (
    <>
      <div className="cal-toolbar">
        <div className="cal-nav">
          <button type="button" className="icon-btn" onClick={() => setPickedYear(ly - 1)} aria-label="Previous leave year" disabled={ly <= minYear}>
            <Icon name="chevron-left" />
          </button>
          <h2 className="cal-month" aria-live="polite">{leaveYearLabel(ly, startMonth)}</h2>
          <button type="button" className="icon-btn" onClick={() => setPickedYear(ly + 1)} aria-label="Next leave year">
            <Icon name="chevron-right" />
          </button>
        </div>
        {isHR && (
          <div className="inline-field">
            <label htmlFor="plan-user">Person</label>
            <Avatar name={user.name} photoUrl={user.photoUrl} />
            <select id="plan-user" value={user.id} onChange={(e) => setPickedUser(e.target.value)}>
              {ctx.activeUsers.slice().sort(byName).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        )}
      </div>

      <dl className="stats stats-inline card">
        <div><dt>Allowance</dt><dd>{formatDays(bal.allowance)}</dd></div>
        <div><dt><span className="key key-approved" />Approved</dt><dd>{formatDays(bal.approved)}</dd></div>
        <div><dt><span className="key key-pending" />Pending</dt><dd>{formatDays(bal.pending)}</dd></div>
        <div><dt>Remaining</dt><dd>{formatDays(bal.remaining)}</dd></div>
      </dl>

      <div className="cal-scroll card" tabIndex={0} role="region" aria-label={`Year planner for ${user.name}. Scrolls sideways on small screens.`}>
        <table className="cal planner">
          <caption className="sr-only">{user.name}'s leave for the year {leaveYearLabel(ly, startMonth)}. Each row is a month.</caption>
          <thead>
            <tr>
              <th scope="col" className="person head-corner">Month</th>
              {Array.from({ length: 31 }, (_, i) => (
                <th key={i} scope="col" className="dayhead"><span className="dom">{i + 1}</span></th>
              ))}
              <th scope="col" className="total-col">Holiday</th>
            </tr>
          </thead>
          <tbody>
            {months.map(({ m, y }) => {
              const dim = daysInMonth(y, m);
              const range = { from: monthISO(y, m, 1), to: monthISO(y, m, dim) };
              const holidayDays = mine.filter((r) => typeOf(r.typeId).deducts).reduce((sum, r) => sum + requestDays(r, ctx.bh, range), 0);
              return (
                <tr key={`${y}-${m}`}>
                  <th scope="row" className="person"><span className="pname">{monthName(m)}</span><span className="muted"> {y}</span></th>
                  {Array.from({ length: 31 }, (_, i) => {
                    if (i + 1 > dim) return <td key={i} className="day blank" />;
                    const d = monthISO(y, m, i + 1);
                    const cls = [isNonWorking(d, ctx.bh) && !ctx.bh.has(d) ? 'we' : '', ctx.bh.has(d) ? 'bh' : '', d === ctx.today ? 'today' : ''].filter(Boolean).join(' ');
                    const leaves = isNonWorking(d, ctx.bh)
                      ? []
                      : mine.map((r) => ({ r, p: partOnDay(r, d) })).filter((x): x is { r: LeaveRequest; p: NonNullable<typeof x.p> } => !!x.p);
                    return (
                      <td key={i} className={`day ${cls}`.trim()} title={ctx.bh.get(d)}>
                        <div className="cell">
                          {leaves.map(({ r, p }) => {
                            const t = displayType(r, ctx.me, ctx);
                            const label = `${t.label}, ${fmtRange(r.start, r.end)}, ${r.status}`;
                            return (
                              <button
                                key={r.id}
                                type="button"
                                className={`lv lv-${p} ${r.status === 'pending' ? 'lv-pending' : ''}`.trim()}
                                style={typeStyle(t)}
                                data-l={t.letter}
                                onClick={() => openDetail(r.id)}
                                aria-label={label}
                                title={label}
                              />
                            );
                          })}
                        </div>
                      </td>
                    );
                  })}
                  <td className="total-col">{holidayDays ? formatDays(holidayDays) : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="hint">Weekends and bank holidays are shaded and never count against your allowance. Hover or tap a block for details.</p>
    </>
  );
}
